import * as React from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router';
import ImagePicker from '../components/ImagePicker';
import { RichTextEditor } from '../components/RichTextEditor';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

export default function ProductAdd() {
  const navigate = useNavigate();

  const [product, setProduct] = React.useState<any>({
    name: '',
    description: '',
    characteristic: '',
    compose: '',
    categoryId: '',
    subCategoryId: '',
    defaultPrice: '',
    defaultBiggerPrice: '',
    productsCarouselId: null,
    isActive: true,
    promoPrice: '',
    isPromo: false,
  });

  const [categories, setCategories] = React.useState<any[]>([]);
  const [subCategories, setSubCategories] = React.useState<any[]>([]);
  const [carousels, setCarousels] = React.useState<any[]>([]);
  const [prices, setPrices] = React.useState<any[]>([]);
  const [enableVariations, setEnableVariations] = React.useState(false);
  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);

  React.useEffect(() => {
    fetch(`${API_URL}/categories`)
      .then((r) => r.json())
      .then(setCategories);
    fetch(`${API_URL}/sub-categories`)
      .then((r) => r.json())
      .then((r) => setSubCategories(r.data));
    fetch(`${API_URL}/carousel?perPage=1000`)
      .then((r) => r.json())
      .then((r) => setCarousels(r.data));
  }, []);

  const filteredSubCategories = subCategories.filter(
    (s) => s.categoryId === product.categoryId,
  );

  const updatePrice = (index: number, field: string, value: any) => {
    const next = [...prices];
    next[index] = { ...next[index], [field]: value };
    setPrices(next);
  };

  const submit = async () => {
    const formData = new FormData();
    formData.append('name', product.name);
    formData.append('description', product.description);
    formData.append('characteristic', product.characteristic);
    formData.append('compose', product.compose);
    formData.append('categoryId', product.categoryId ?? '');
    formData.append('subCategoryId', product.subCategoryId ?? '');
    formData.append('isActive', product.isActive ? 'true' : 'false');
    formData.append('isPromo', product.isPromo ? 'true' : 'false');
    if (!enableVariations) {
      formData.append('defaultPrice', product.defaultPrice ?? '');
      formData.append('defaultBiggerPrice', product.defaultBiggerPrice ?? '');
      formData.append(
        'promoPrice',
        product.isPromo ? (product.promoPrice ?? 0) : '',
      );
    }
    if (enableVariations) {
      const normalizedPrices = prices.map((p) => ({
        ...p,
        promoPrice: product.isPromo ? p.promoPrice : null,
      }));
      formData.append('prices', JSON.stringify(normalizedPrices));
    }
    if (product.productsCarouselId)
      formData.append('productsCarouselId', product.productsCarouselId);
    if (image) formData.append('image', image);
    else if (mediaId) formData.append('mediaId', String(mediaId));

    await fetch(`${API_URL}/products`, { method: 'POST', body: formData });
    navigate('/products');
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <TextField
        fullWidth
        label="Назва"
        value={product.name}
        onChange={(e) => setProduct({ ...product, name: e.target.value })}
        margin="normal"
        sx={{ mt: 2, mb: 2 }}
      />
      <RichTextEditor
        value={product.description}
        onChange={(html) => setProduct({ ...product, description: html })}
        label="Опис"
        minRows={4}
        sx={{ mb: 2 }}
      />
      <RichTextEditor
        value={product.characteristic}
        onChange={(html) => setProduct({ ...product, characteristic: html })}
        label="Характеристики"
        minRows={4}
        sx={{ mb: 2 }}
      />
      <RichTextEditor
        value={product.compose}
        onChange={(html) => setProduct({ ...product, compose: html })}
        label="Склад"
        minRows={4}
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={product.isActive}
            onChange={(e) =>
              setProduct({ ...product, isActive: e.target.checked })
            }
          />
        }
        label="Відобразити продукт у боті"
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Категорія</InputLabel>
        <Select
          value={product.categoryId}
          onChange={(e) =>
            setProduct({
              ...product,
              categoryId: e.target.value,
              subCategoryId: '',
            })
          }
        >
          {categories.map((c) => (
            <MenuItem key={c.categoryId} value={c.categoryId}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {filteredSubCategories.length > 0 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Підкатегорія</InputLabel>
          <Select
            value={product.subCategoryId}
            onChange={(e) =>
              setProduct({ ...product, subCategoryId: e.target.value })
            }
          >
            {filteredSubCategories.map((s) => (
              <MenuItem key={s.subCategoryId} value={s.subCategoryId}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={enableVariations}
            onChange={(e) => {
              const checked = e.target.checked;
              setEnableVariations(checked);
              if (checked && prices.length === 0)
                setPrices([
                  { weight: '', price: '', biggerPrice: '', promoPrice: '' },
                ]);
            }}
          />
        }
        label="Використовувати ціни варіації"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={product.isPromo}
            onChange={(e) =>
              setProduct({ ...product, isPromo: e.target.checked })
            }
          />
        }
        label="Це акційний товар"
      />
      {!enableVariations && product.isPromo && (
        <TextField
          type="number"
          fullWidth
          label="Акційна ціна"
          value={product.promoPrice ?? ''}
          onChange={(e) =>
            setProduct({
              ...product,
              promoPrice: e.target.value === '' ? '' : +e.target.value,
            })
          }
          sx={{ mb: 3 }}
        />
      )}
      {!enableVariations && (
        <Box sx={{ mb: 3 }}>
          <TextField
            type="number"
            fullWidth
            label="Ціна"
            value={product.defaultPrice ?? ''}
            onChange={(e) =>
              setProduct({
                ...product,
                defaultPrice: e.target.value === '' ? '' : +e.target.value,
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            type="number"
            fullWidth
            label="Підвищена ціна"
            value={product.defaultBiggerPrice ?? ''}
            onChange={(e) =>
              setProduct({
                ...product,
                defaultBiggerPrice:
                  e.target.value === '' ? '' : +e.target.value,
              })
            }
          />
        </Box>
      )}
      {enableVariations &&
        prices.map((p, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}
          >
            <TextField
              label="Вага"
              value={p.weight}
              onChange={(e) => updatePrice(i, 'weight', e.target.value)}
            />
            <TextField
              type="number"
              label="Ціна"
              value={p.price ?? ''}
              onChange={(e) =>
                updatePrice(
                  i,
                  'price',
                  e.target.value === '' ? '' : +e.target.value,
                )
              }
            />
            <TextField
              type="number"
              label="Підвищена ціна"
              value={p.biggerPrice ?? ''}
              onChange={(e) =>
                updatePrice(
                  i,
                  'biggerPrice',
                  e.target.value === '' ? '' : +e.target.value,
                )
              }
            />
            {product.isPromo && (
              <TextField
                type="number"
                label="Акційна ціна"
                value={p.promoPrice ?? ''}
                onChange={(e) =>
                  updatePrice(
                    i,
                    'promoPrice',
                    e.target.value === '' ? '' : +e.target.value,
                  )
                }
              />
            )}
            <IconButton
              onClick={() =>
                setPrices(prices.filter((_, index) => index !== i))
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}
      {enableVariations && (
        <Button
          variant="outlined"
          onClick={() =>
            setPrices([
              ...prices,
              { weight: '', price: '', biggerPrice: '', promoPrice: '' },
            ])
          }
        >
          Додати варіацію
        </Button>
      )}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Каталог</InputLabel>
        <Select
          style={{ height: 80 }}
          value={product.productsCarouselId}
          onChange={(e) =>
            setProduct({ ...product, productsCarouselId: e.target.value })
          }
        >
          {carousels.map((c) => (
            <MenuItem key={c.productsCarouselId} value={c.productsCarouselId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <img src={`${IMAGE_URL}/${c.image}`} width={48} height={48} />
                <Typography>#{c.productsCarouselId}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {product.productsCarouselId && (
          <Box sx={{ mt: 1 }}>
            <a
              href={`/carousel/${product.productsCarouselId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: '#1976d2' }}
            >
              Переглянути каталог №{product.productsCarouselId}
            </a>
          </Box>
        )}
      </FormControl>
      <ImagePicker
        title="Зображення продукту"
        value={imageUrl || ''}
        onChange={(value) => {
          if (value.type === 'file') {
            setImage(value.file);
            setMediaId(null);
            setImageUrl(URL.createObjectURL(value.file));
          }
          if (value.type === 'media') {
            setImage(null);
            setMediaId(value.mediaId);
            setImageUrl(value.url);
          }
        }}
      />
      <Button variant="contained" onClick={submit}>
        Додати продукт
      </Button>
    </Box>
  );
}
